/**
 * @file appFeedbackService.ts
 * @brief Product bug / feedback reports submitted from the app.
 */

import apiClient, { unwrapResponse } from './apiClient';
import { API_ENDPOINTS } from './endpoints';

export type AppFeedbackType = 'bug' | 'feedback' | 'question';
export type AppFeedbackSeverity = 'low' | 'medium' | 'high' | 'critical';
export type AppFeedbackStatus = 'open' | 'in_progress' | 'resolved' | 'closed';

export interface AppFeedback {
  id: string;
  type: AppFeedbackType;
  title: string;
  description: string;
  severity?: AppFeedbackSeverity;
  pagePath?: string;
  status: AppFeedbackStatus;
  adminNotes?: string;
  createdBy: string;
  createdByName?: string;
  resolvedBy?: string;
  resolvedByName?: string;
  resolvedAt?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateAppFeedbackPayload {
  type: AppFeedbackType;
  title: string;
  description: string;
  severity?: AppFeedbackSeverity;
  pagePath?: string;
}

function mapRaw(raw: Record<string, unknown>): AppFeedback {
  return {
    id: String(raw.id ?? ''),
    type: (raw.type as AppFeedbackType) || 'bug',
    title: String(raw.title ?? ''),
    description: String(raw.description ?? ''),
    severity: raw.severity != null ? (raw.severity as AppFeedbackSeverity) : undefined,
    pagePath: raw.pagePath != null ? String(raw.pagePath) : undefined,
    status: (raw.status as AppFeedbackStatus) || 'open',
    adminNotes: raw.adminNotes != null ? String(raw.adminNotes) : undefined,
    createdBy: String(raw.createdBy ?? ''),
    createdByName: raw.createdByName != null ? String(raw.createdByName) : undefined,
    resolvedBy: raw.resolvedBy != null ? String(raw.resolvedBy) : undefined,
    resolvedByName: raw.resolvedByName != null ? String(raw.resolvedByName) : undefined,
    resolvedAt: raw.resolvedAt != null ? String(raw.resolvedAt) : undefined,
    createdAt: String(raw.createdAt ?? ''),
    updatedAt: raw.updatedAt != null ? String(raw.updatedAt) : undefined,
  };
}

export async function createAppFeedback(payload: CreateAppFeedbackPayload): Promise<AppFeedback> {
  const response = await apiClient.post(API_ENDPOINTS.APP_FEEDBACK.CREATE, payload);
  return mapRaw(unwrapResponse<Record<string, unknown>>(response));
}

export async function getMyAppFeedback(): Promise<AppFeedback[]> {
  const response = await apiClient.get(API_ENDPOINTS.APP_FEEDBACK.MINE);
  const data = unwrapResponse<Record<string, unknown>[]>(response);
  return Array.isArray(data) ? data.map(mapRaw) : [];
}

export async function listAppFeedback(filters?: {
  status?: AppFeedbackStatus;
  type?: AppFeedbackType;
}): Promise<AppFeedback[]> {
  const response = await apiClient.get(API_ENDPOINTS.APP_FEEDBACK.LIST, { params: filters });
  const data = unwrapResponse<Record<string, unknown>[]>(response);
  return Array.isArray(data) ? data.map(mapRaw) : [];
}

export async function updateAppFeedback(
  id: string,
  payload: { status?: AppFeedbackStatus; adminNotes?: string }
): Promise<AppFeedback> {
  const response = await apiClient.patch(API_ENDPOINTS.APP_FEEDBACK.UPDATE(id), payload);
  return mapRaw(unwrapResponse<Record<string, unknown>>(response));
}

export const appFeedbackService = {
  create: createAppFeedback,
  getMine: getMyAppFeedback,
  list: listAppFeedback,
  update: updateAppFeedback,
};

export default appFeedbackService;
